/**
 * One normalised view of an engagement run, from either of two sources.
 *
 * A run is observable two ways, and a user meets both in a single engagement:
 *
 *   the SSE stream        rich and live — a row per turn as it transitions.
 *                         Available only to the client that started the run.
 *   the tracking document live AND durable, because agent-svc re-rolls it after
 *                         every turn. The source a reload should use.
 *   the project document  counters, but written only at begin (0) and finish, so
 *                         it reads "0 of 7" for the whole run. Kept as the last
 *                         resort: it is the one thing a client always has, since
 *                         the stage is derived from it.
 *
 * Both normalise to `AdvisoryRunView` so exactly one component renders them.
 * That is the whole point of this module: without it the reload path would grow
 * a second, near-identical progress view that drifts from the first.
 *
 * A fourth source would be one more adapter here and no change to the component.
 *
 * Each adapter also JOINS the question text onto its rows, because a run named by
 * label alone (`root`, `earnings-quality`, `earnings-quality/sq2`) is unreadable
 * to the person waiting on it. The text is never invented here: the tracking
 * source reads the `plan` agent-svc writes before the first turn is posted, and
 * the stream source reads the confirmed key questions the page already holds. A
 * step no source names keeps its label, which is what every row used to show.
 */

import { compareKeyQuestionOrder } from '../../redux/slices/advisory/advisoryTypes';
import type { AdvisoryStage, KeyQuestionDoc } from '../../redux/slices/advisory/advisoryTypes';
import type { SupervisorAnalysisState } from '../../hooks/advisory/useSupervisorTurn';

/**
 * One earlier step a turn retrieved from.
 *
 * Both halves are carried because the pair is what makes a retrieval legible:
 * `label` is the internal slug the tracking record and the server logs use
 * (`earnings-quality/sq1`), and `question` is the only half a reader can act on.
 * `question` is `''` when no source named that step — the component shows the
 * slug then rather than an empty line.
 */
export interface AdvisoryRunTurnSource {
  label: string;
  question: string;
  /**
   * The source step's own phase, so a reader can be told what KIND of thing it
   * read — the framing step, another key question — without being handed the
   * label. `''` when the run's turn records do not name it.
   */
  phase: string;
}

/** One turn's row. Only the stream can populate these. */
export interface AdvisoryRunTurn {
  label: string;
  /** `1-root`, `2-key-questions`, `3-subquestions` — the phase it belongs to. */
  phase: string;
  status: string;
  failed: boolean;
  /** What retrieval DECIDED for this turn. */
  edges: number;
  /**
   * Injected memory slots that arrived empty. Non-zero means retrieval reported
   * success and delivered nothing — the signature that hid a total delivery
   * outage behind healthy scores for 184 V3 executions.
   */
  blankSlots: number;
  /**
   * What this step actually ASKED.
   *
   * The progress list used to name each step by its label alone, so a run read
   * `root` / `earnings-quality` / `earnings-quality/sq2` and left the person
   * waiting on it to guess what any of that meant. The text comes from the run
   * document's own `plan`, which agent-svc writes before the first turn is
   * posted — so it is available from the first second of a run rather than once
   * a turn completes.
   *
   * `''` when no source named it (the stream before any key question is known,
   * or a label the plan does not carry). The component falls back to the label
   * rather than rendering a blank row.
   */
  question: string;
  /** The key question this step hangs off. `''` for the framing step. */
  keyQuestionId: string;
  /**
   * The key question's 1-based position in the run's plan, so a row can carry
   * the same `KQ 2` marker the question editor showed. `null` for the framing
   * step and for any step no source named.
   */
  keyQuestionPosition: number | null;
  /** 1-based position in its chain; `null` for a framing or key-question step. */
  subquestionN: number | null;
  /** Wall time for the turn, or `null` when the source never measured one. */
  wallSeconds: number | null;
  /**
   * The earlier steps whose answers this turn retrieved.
   *
   * NOT a second spelling of `edges`: `edges` counts retrieved passages, and
   * fifteen of them routinely come from two steps. Empty from the stream, which
   * reports the count but never where it came from.
   */
  sources: AdvisoryRunTurnSource[];
}

export interface AdvisoryRunView {
  runId: string;
  /** `''` while in flight; otherwise the run's terminal status. */
  status: string;
  /** True once a terminal failure arrived. Never carries the message (ASVS V7). */
  failed: boolean;
  plannedTurns: number;
  completedTurns: number;
  failedTurns: number;
  /** Rows, when the source has them. Empty from the document path. */
  turns: AdvisoryRunTurn[];
  /**
   * Evidence totals, or `null` when the source cannot know them. `null` is not
   * zero: a document-sourced view has never measured them, and rendering `0`
   * would claim retrieval delivered nothing rather than that nothing was read.
   */
  totalEdges: number | null;
  totalBlankSlots: number | null;
}

/**
 * One planned step, keyed by the label every source reports it under.
 *
 * The run document's `plan` is the only place the question TEXT and the label
 * meet, which is what makes this index the join. agent-svc writes the plan
 * before it posts a single turn, deliberately so "a frontend can render the
 * whole engagement as a progress tree from the first second" — this module is
 * what finally reads it.
 */
interface PlannedStep {
  question: string;
  keyQuestionId: string;
  keyQuestionPosition: number | null;
  subquestionN: number | null;
  phase: string;
}

/** What an unnamed step resolves to. Never a guess, and never a fabricated position. */
const UNPLANNED: PlannedStep = {
  question: '',
  keyQuestionId: '',
  keyQuestionPosition: null,
  subquestionN: null,
  phase: '',
};

const PHASE_ROOT = '1-root';
const PHASE_KEY_QUESTIONS = '2-key-questions';
const PHASE_SUBQUESTIONS = '3-subquestions';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
/** `null`, not `0`: an unmeasured wall clock must not render as an instant turn. */
const numOrNull = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The index the tracking source builds, from the run document's `plan`.
 *
 * Positions come from the branch's place in the plan, NOT from its `order`
 * field: `order` is the server's topological sort of the dependency graph and is
 * under no obligation to start at 1 or run contiguously, whereas the plan's
 * branches are already in that order — so `branchIndex + 1` is the same `KQ n`
 * the question editor rendered. It agrees with the editor for every question
 * that ran; a question the plan skipped or deferred shifts the ones after it,
 * which is exactly why the question TEXT is the row's primary line and the
 * number is only a cross-reference.
 */
function planIndex(planValue: unknown, rootQuestion = ''): Map<string, PlannedStep> {
  const index = new Map<string, PlannedStep>();
  const plan = isRecord(planValue) ? planValue : null;
  if (!plan) {
    return index;
  }

  // The framing step's question is the engagement's own. The plan names the label
  // it runs under but carries no text for it, because the text belongs to the
  // user: `userQuestion` is what they asked, `goal` the extracted version of it.
  index.set(str(plan.rootLabel) || 'root', {
    ...UNPLANNED,
    question: rootQuestion,
    phase: PHASE_ROOT,
  });

  const branches = Array.isArray(plan.branches) ? plan.branches : [];
  branches.forEach((rawBranch, branchIndex) => {
    if (!isRecord(rawBranch)) {
      return;
    }
    const keyQuestionId = str(rawBranch.keyQuestionId);
    if (!keyQuestionId) {
      return;
    }
    const keyQuestionPosition = branchIndex + 1;
    index.set(keyQuestionId, {
      question: str(rawBranch.question),
      keyQuestionId,
      keyQuestionPosition,
      subquestionN: null,
      phase: PHASE_KEY_QUESTIONS,
    });

    const subquestions = Array.isArray(rawBranch.subquestions) ? rawBranch.subquestions : [];
    subquestions.forEach((rawSub, subIndex) => {
      if (!isRecord(rawSub)) {
        return;
      }
      const n = num(rawSub.n) || subIndex + 1;
      index.set(str(rawSub.label) || `${keyQuestionId}/sq${n}`, {
        question: str(rawSub.text),
        keyQuestionId,
        keyQuestionPosition,
        subquestionN: n,
        phase: PHASE_SUBQUESTIONS,
      });
    });
  });

  return index;
}

function planIndexFromRunDoc(runDoc: Record<string, unknown>): Map<string, PlannedStep> {
  return planIndex(runDoc.plan, str(runDoc.userQuestion) || str(runDoc.goal));
}

/**
 * The same index for the stream source, built from the key-question set the page
 * already holds.
 *
 * The stream reports a turn's `label` and `keyQuestionId` but never the question
 * text, and it is the only source available in the window before the tracking
 * document's first write. `${id}/sq${n}` is agent-svc's own label convention for
 * a chain step (`plan.py`), reproduced here rather than invented — a divergence
 * would show up as a follow-up row with no question text, never as wrong text.
 */
function planIndexFromKeyQuestions(
  keyQuestions: readonly KeyQuestionDoc[]
): Map<string, PlannedStep> {
  const index = new Map<string, PlannedStep>();
  [...keyQuestions].sort(compareKeyQuestionOrder).forEach((keyQuestion, position) => {
    const keyQuestionPosition = position + 1;
    index.set(keyQuestion.id, {
      question: keyQuestion.question,
      keyQuestionId: keyQuestion.id,
      keyQuestionPosition,
      subquestionN: null,
      phase: PHASE_KEY_QUESTIONS,
    });
    (keyQuestion.subquestions ?? []).forEach((text, subIndex) => {
      const n = subIndex + 1;
      index.set(`${keyQuestion.id}/sq${n}`, {
        question: text,
        keyQuestionId: keyQuestion.id,
        keyQuestionPosition,
        subquestionN: n,
        phase: PHASE_SUBQUESTIONS,
      });
    });
  });
  return index;
}

const turnFailed = (turn: AdvisoryRunTurn): boolean =>
  turn.failed ||
  turn.status === 'failed' ||
  turn.status === 'rejected' ||
  turn.status === 'timeout';

function plannedTurn(label: string, planned: PlannedStep): AdvisoryRunTurn {
  return {
    label,
    phase: planned.phase,
    status: 'planned',
    failed: false,
    edges: 0,
    blankSlots: 0,
    question: planned.question,
    keyQuestionId: planned.keyQuestionId,
    keyQuestionPosition: planned.keyQuestionPosition,
    subquestionN: planned.subquestionN,
    wallSeconds: null,
    sources: [],
  };
}

/**
 * Materialise the whole plan, then overlay the turns that have actually started.
 *
 * agent-svc only creates a turn record when it reaches that step. In particular,
 * key-question records do not exist while the framing turn runs, and a failed
 * step prevents every later step in its chain from ever receiving a record. The
 * plan is therefore the row set; observed turns are state updates for that set.
 */
function mergePlannedTurns(
  index: ReadonlyMap<string, PlannedStep>,
  observed: readonly AdvisoryRunTurn[],
  runStatus: string
): AdvisoryRunTurn[] {
  const observedByLabel = new Map(observed.map(turn => [turn.label, turn]));
  const observedLabels = new Set(observed.map(turn => turn.label));
  const merged = Array.from(index.entries()).map(
    ([label, planned]) => observedByLabel.get(label) ?? plannedTurn(label, planned)
  );

  // Keep a server-reported turn even if an older or malformed plan did not name
  // it. The plan owns the expected shape, but it must never erase observed work.
  for (const turn of observed) {
    if (!index.has(turn.label)) {
      merged.push(turn);
    }
  }

  const markSkipped = (turn: AdvisoryRunTurn) => {
    if (!observedLabels.has(turn.label) && turn.status === 'planned') {
      turn.status = 'skipped';
    }
  };

  const root = merged.find(turn => turn.phase === PHASE_ROOT);
  if (root && turnFailed(root)) {
    merged.filter(turn => turn !== root).forEach(markSkipped);
  } else {
    const branches = new Map<string, AdvisoryRunTurn[]>();
    for (const turn of merged) {
      if (!turn.keyQuestionId) {
        continue;
      }
      const branch = branches.get(turn.keyQuestionId);
      if (branch) {
        branch.push(turn);
      } else {
        branches.set(turn.keyQuestionId, [turn]);
      }
    }

    branches.forEach(branch => {
      let blocked = false;
      for (const turn of [...branch].sort(
        (a, b) => (a.subquestionN ?? 0) - (b.subquestionN ?? 0)
      )) {
        if (blocked) {
          markSkipped(turn);
        }
        if (turnFailed(turn)) {
          blocked = true;
        }
      }
    });
  }

  // A terminal run cannot have queued work. Anything in the plan that never
  // received a record was not run (for example after an unexpected branch exit).
  if (runStatus === 'completed' || runStatus === 'partial' || runStatus === 'failed') {
    merged.forEach(markSkipped);
  }

  return merged;
}

/** Actual execution time from pgms' persisted timestamps, never watcher wait time. */
function elapsedSeconds(d: Record<string, unknown>): number | null {
  const startedAt = str(d.startedAt);
  const completedAt = str(d.completedAt);
  if (!startedAt || !completedAt) {
    return null;
  }
  const started = Date.parse(startedAt);
  const completed = Date.parse(completedAt);
  if (!Number.isFinite(started) || !Number.isFinite(completed) || completed < started) {
    return null;
  }
  return (completed - started) / 1000;
}

/** The live view, from the SSE stream. `null` before a run has started. */
export function runViewFromStream(
  analysis: SupervisorAnalysisState | null,
  /**
   * The confirmed key questions, for the question text the stream never sends.
   * Optional: a caller that has not got them yet gets rows named by label, which
   * is what this view showed for every row before the text was joined on at all.
   */
  keyQuestions: readonly KeyQuestionDoc[] = []
): AdvisoryRunView | null {
  if (!analysis) {
    return null;
  }
  const questionIndex = planIndexFromKeyQuestions(keyQuestions);
  const plannedIndex = planIndex(analysis.plan);
  // The event plan is the authority on which capped/skipped questions this run
  // will execute. The confirmed set remains a text fallback for older streams.
  plannedIndex.forEach((planned, label) => {
    const question = questionIndex.get(label);
    if (!planned.question && question?.question) {
      plannedIndex.set(label, { ...planned, question: question.question });
    }
  });
  const index = plannedIndex.size > 0 ? plannedIndex : questionIndex;
  const observedTurns = analysis.turns.map(t => {
    const planned = index.get(t.label) ?? UNPLANNED;
    return {
      label: t.label,
      phase: t.phase,
      status: t.status,
      failed: t.failed,
      edges: t.totalEdges,
      blankSlots: t.blankSlots,
      question: planned.question,
      // The stream reports these two on the turn itself, so they hold even for
      // a label the key-question set does not name. The position cannot be:
      // it is a place in an ordered set, and only the set can say what it is.
      keyQuestionId: t.keyQuestionId || planned.keyQuestionId,
      subquestionN: t.subquestionN ?? planned.subquestionN,
      keyQuestionPosition: planned.keyQuestionPosition,
      // `wallSeconds` starts after the POST returns. That excludes the whole
      // execution on synchronous/local runs, so it is not elapsed time and is
      // deliberately not rendered. Tracking can derive real time from stamps.
      wallSeconds: null,
      // The stream counts retrieved passages and never says where they came
      // from. Empty is the honest answer; the tracking source has the origins.
      sources: [],
    };
  });
  const turns =
    plannedIndex.size > 0
      ? mergePlannedTurns(plannedIndex, observedTurns, analysis.status)
      : observedTurns;
  return {
    runId: analysis.runId,
    status: analysis.status,
    failed: analysis.failed,
    plannedTurns: analysis.plannedTurns,
    // Counted from the rows rather than read off the terminal frame, so the
    // number moves while the run is in flight instead of jumping at the end.
    completedTurns: analysis.turns.filter(t => t.status === 'completed').length,
    failedTurns: analysis.turns.filter(t => t.failed).length,
    turns,
    totalEdges: analysis.totalEdges,
    totalBlankSlots: analysis.totalBlankSlots,
  };
}

/**
 * The recovered view, from the stage the project document derives. `null` for
 * every stage that is not a run — which is what lets a caller use this as the
 * test for "is there a run to show" rather than branching on tags itself.
 *
 * `analysisPrepared` deliberately returns `null`: a conversation exists and
 * documents may be uploaded, but nothing is running, so there is no progress to
 * report and showing a 0-of-0 run would be a lie.
 */
export function runViewFromStage(stage: AdvisoryStage): AdvisoryRunView | null {
  const base = { turns: [], totalEdges: null, totalBlankSlots: null };
  switch (stage.tag) {
    case 'analysisRunning':
      return {
        ...base,
        runId: stage.runId,
        status: '',
        failed: false,
        // `plannedTurns: 0` DELIBERATELY, even though the stage carries a real
        // number — it is what makes the component hide the progress bar.
        //
        // The project document's `completedTurns` is written exactly twice: `0` when
        // the run begins and the final count when it ends. So mid-run this source
        // knows a run is going and knows NOTHING about how far along it is, and a bar
        // fed from it renders a confident "0 of 7" for twenty minutes while turns are
        // completing. That is worse than no bar: it reads as a stalled run.
        //
        // `runViewFromTracking` is the source that can answer this, and the caller
        // prefers it. This branch is the honest last resort: a run exists, and its
        // progress is unknown.
        plannedTurns: 0,
        completedTurns: 0,
        failedTurns: 0,
      };
    case 'analysisComplete':
      return {
        ...base,
        runId: stage.runId,
        status: 'completed',
        failed: false,
        plannedTurns: stage.plannedTurns,
        completedTurns: stage.completedTurns,
        failedTurns: 0,
      };
    case 'analysisPartial':
      return {
        ...base,
        runId: stage.runId,
        // Terminal but NOT a failure: a run where one branch died still answered
        // the others, and agent-svc reports it separately so that work is not
        // thrown away.
        status: 'partial',
        failed: false,
        plannedTurns: stage.plannedTurns,
        completedTurns: stage.completedTurns,
        failedTurns: stage.failedTurns,
      };
    case 'analysisFailed':
      return {
        ...base,
        runId: stage.runId,
        status: 'failed',
        failed: true,
        plannedTurns: 0,
        completedTurns: 0,
        failedTurns: 0,
      };
    default:
      return null;
  }
}

/**
 * The live-and-durable view, from the run's own tracking document.
 *
 * The third source, and the one that fixes what the other two cannot. The stream is
 * live but private to the client that started the run; the project document survives
 * but its `completedTurns` is written only at begin (0) and finish, so it reads
 * "0 of 7" for the whole run. This document is re-rolled after every turn, so it is
 * both live AND durable — the only source that is.
 *
 * `turnDocs` is optional: a caller that only needs the counters can skip listening to
 * seven documents. Rows are ordered by phase then label, which is `1-root` →
 * `2-key-questions` → `3-subquestions` and so reads in execution order.
 */
export function runViewFromTracking(
  runDoc: Record<string, unknown> | null,
  turnDocs: readonly Record<string, unknown>[] = []
): AdvisoryRunView | null {
  if (!runDoc) {
    return null;
  }
  const totals =
    runDoc.totals && typeof runDoc.totals === 'object'
      ? (runDoc.totals as Record<string, unknown>)
      : {};
  // `preparing` and `running` are both in-flight; the view's own contract is that an
  // empty status means in flight, so neither is passed through.
  const stored = str(runDoc.status);
  const terminal = stored === 'completed' || stored === 'partial' || stored === 'failed';

  const index = planIndexFromRunDoc(runDoc);

  // Retrieval names its origins by TURN ID, which is meaningless on screen. Every
  // turn record carries both its id and its label, so the run's own records are
  // the lookup — no extra read, and nothing invented for an id that is not there.
  const labelByTurnId = new Map<string, string>();
  const phaseByLabel = new Map<string, string>();
  index.forEach((planned, label) => {
    phaseByLabel.set(label, planned.phase);
  });
  for (const d of turnDocs) {
    const turnId = str(d.turnId);
    if (turnId && str(d.label)) {
      labelByTurnId.set(turnId, str(d.label));
    }
    if (str(d.label)) {
      phaseByLabel.set(str(d.label), str(d.phase));
    }
  }

  /**
   * The earlier steps one turn retrieved from, in reading order.
   *
   * An id with no turn in this run is DROPPED rather than shown raw: it has no
   * row to point at. That loses nothing a reader could use and claims nothing —
   * `edges` still counts every retrieved passage, so a shorter list here never
   * reads as a smaller retrieval.
   */
  const sourcesFor = (d: Record<string, unknown>, ownLabel: string): AdvisoryRunTurnSource[] => {
    const ids = Array.isArray(d.retrievedSourceTurns) ? d.retrievedSourceTurns : [];
    return ids
      .map(id => labelByTurnId.get(str(id)) ?? '')
      .filter(label => Boolean(label) && label !== ownLabel)
      .map(label => ({
        label,
        question: (index.get(label) ?? UNPLANNED).question,
        phase: phaseByLabel.get(label) ?? '',
      }))
      .sort((a, b) => {
        // Framing first, then by key question, then along each chain — the order
        // the reader met these steps in, not the sort order of their turn ids.
        const pa = index.get(a.label) ?? UNPLANNED;
        const pb = index.get(b.label) ?? UNPLANNED;
        return (
          (pa.keyQuestionPosition ?? 0) - (pb.keyQuestionPosition ?? 0) ||
          (pa.subquestionN ?? 0) - (pb.subquestionN ?? 0) ||
          a.label.localeCompare(b.label)
        );
      });
  };

  const observedTurns = turnDocs
    .map(d => {
      const label = str(d.label);
      const planned = index.get(label) ?? UNPLANNED;
      return {
        label,
        phase: str(d.phase),
        status: str(d.status),
        // A boolean, never the message: `error` on the record is raw pgms text.
        failed: Boolean(str(d.error)) || str(d.status) === 'failed',
        edges: num(d.totalEdges),
        blankSlots: num(d.totalBlankSlots),
        question: planned.question,
        // Preferred off the turn record, which states them outright, and derived
        // from the plan only for a record written before those fields existed.
        keyQuestionId: str(d.keyQuestionId) || planned.keyQuestionId,
        subquestionN: numOrNull(d.subquestionN) ?? planned.subquestionN,
        keyQuestionPosition: planned.keyQuestionPosition,
        // The watcher's `wallSeconds` begins after POST. Persisted pgms stamps
        // cover the real execution on both asynchronous and synchronous paths.
        wallSeconds: elapsedSeconds(d),
        sources: sourcesFor(d, label),
      };
    })
    .filter(t => t.label);
  const turns = mergePlannedTurns(index, observedTurns, terminal ? stored : '').sort((a, b) =>
    a.phase === b.phase ? a.label.localeCompare(b.label) : a.phase.localeCompare(b.phase)
  );

  return {
    runId: str(runDoc.runId),
    status: terminal ? stored : '',
    failed: stored === 'failed',
    plannedTurns: num(totals.plannedTurns),
    completedTurns: num(totals.completedTurns),
    failedTurns: num(totals.failedTurns),
    turns,
    totalEdges: num(totals.totalEdges),
    totalBlankSlots: num(totals.totalBlankSlots),
  };
}

/** Whether a view describes a run still in flight. */
export function isRunInFlight(view: AdvisoryRunView): boolean {
  return view.status === '';
}
